import { ChevronDown, ChevronUp, Plus, Trash2, CheckCircle } from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'
import { formatAmount, formatAmountLive, parseAmount } from '../../utils/amountFormatter'
import type { CT600SupplementaryState } from '../../types/ct600Filing'

function CurrencyInput({ label, value, onChange, readOnly = false }: {
  label: string
  value: string
  onChange: (val: string) => void
  readOnly?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const cursorRef = useRef<number>(0)
  const displayValue = readOnly
    ? (value ? formatAmount(value) : '')
    : (value ? formatAmountLive(value) : '')

  useLayoutEffect(() => {
    const el = inputRef.current
    if (el && document.activeElement === el) {
      el.setSelectionRange(cursorRef.current, cursorRef.current)
    }
  }, [displayValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target
    const pos = el.selectionStart || 0
    let digitsBefore = 0
    for (let i = 0; i < pos; i++) {
      if (el.value[i] !== ',') digitsBefore++
    }
    const raw = parseAmount(el.value)
    const formatted = raw ? formatAmountLive(raw) : ''
    let newPos = formatted.length
    let count = 0
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== ',') count++
      if (count === digitsBefore) { newPos = i + 1; break }
    }
    cursorRef.current = newPos
    onChange(raw)
  }

  return (
    <div>
      <label className='mb-2 block text-sm font-medium text-gray-900'>{label}</label>
      <div className='relative'>
        <span className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400'>£</span>
        <input
          ref={inputRef}
          type='text'
          value={displayValue}
          onChange={handleChange}
          onBlur={(e) => {
            const clean = parseAmount(e.target.value)
            if (clean) {
              const num = parseFloat(clean)
              if (!isNaN(num)) onChange(num.toFixed(2))
            }
          }}
          readOnly={readOnly}
          placeholder='0.00'
          className={`w-full rounded-lg border px-4 py-2.5 pl-8 text-sm text-right ${
            readOnly
              ? 'border-gray-200 bg-gray-50 text-gray-600'
              : 'border-gray-300 text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
          }`}
        />
      </div>
    </div>
  )
}

interface SupplementaryCardProps {
  id: string
  title: string
  description: string
  enabled: boolean
  onToggle: (enabled: boolean) => void
  children: React.ReactNode
}

function SupplementaryCard({ id, title, description, enabled, onToggle, children }: SupplementaryCardProps) {
  const [isOpen, setIsOpen] = useState(enabled)

  return (
    <div className={`rounded-lg border-2 transition-colors ${enabled ? 'border-gray-900' : 'border-gray-200'}`}>
      <div className='flex items-start gap-4 p-5 text-start'>
        <input
          id={id}
          type='checkbox'
          checked={enabled}
          onChange={(e) => {
            onToggle(e.target.checked)
            if (e.target.checked) setIsOpen(true)
          }}
          className='mt-1 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900'
        />
        <div className='flex-1'>
          <label htmlFor={id} className='block text-sm font-semibold text-gray-900 cursor-pointer'>
            {title}
          </label>
          <p className='mt-0.5 text-xs text-gray-500'>{description}</p>
        </div>
        {enabled && (
          <div className='flex items-center gap-2'>
            <CheckCircle className='h-4 w-4 text-green-600' />
            <button
              type='button'
              onClick={() => setIsOpen(!isOpen)}
              className='text-gray-400 hover:text-gray-600'
            >
              {isOpen ? <ChevronUp className='h-5 w-5' /> : <ChevronDown className='h-5 w-5' />}
            </button>
          </div>
        )}
      </div>
      {enabled && isOpen && (
        <div className='border-t border-gray-200 p-5'>
          {children}
        </div>
      )}
    </div>
  )
}

interface Props {
  supplementary: CT600SupplementaryState
  onSupplementaryChange: (update: CT600SupplementaryState) => void
  isCheckingRequirements: boolean
}

export default function CT600StepSupplementary({ supplementary, onSupplementaryChange, isCheckingRequirements }: Props) {
  const updateRequirement = (key: keyof CT600SupplementaryState['requirements'], value: boolean) => {
    onSupplementaryChange({
      ...supplementary,
      requirements: { ...supplementary.requirements, [key]: value },
    })
  }

  const updateCt600a = (field: string, value: unknown) => {
    onSupplementaryChange({
      ...supplementary,
      ct600a: { ...supplementary.ct600a, [field]: value },
    })
  }

  const updateCt600c = (field: string, value: string) => {
    onSupplementaryChange({
      ...supplementary,
      ct600c: { ...supplementary.ct600c, [field]: value },
    })
  }

  const updateCt600l = (field: string, value: string) => {
    onSupplementaryChange({
      ...supplementary,
      ct600l: { ...supplementary.ct600l, [field]: value },
    })
  }

  const addLoan = () => {
    const newLoans = [
      ...supplementary.ct600a.outstandingLoans,
      { participatorName: '', participatorAddress: '', loanAmount: '', dateOfLoan: '' },
    ]
    updateCt600a('outstandingLoans', newLoans)
  }

  const removeLoan = (index: number) => {
    const newLoans = supplementary.ct600a.outstandingLoans.filter((_, i) => i !== index)
    updateCt600a('outstandingLoans', newLoans)
  }

  const updateLoan = (index: number, field: string, value: string) => {
    const newLoans = supplementary.ct600a.outstandingLoans.map((loan, i) => {
      if (i !== index) return loan
      // Spread rather than rebuilding field-by-field — a rebuild silently
      // drops any loan field not listed here.
      return { ...loan, [field]: value }
    })
    updateCt600a('outstandingLoans', newLoans)
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-bold text-gray-900'>Supplementary Pages</h2>
        <p className='mt-1 text-sm text-gray-600'>
          Select and complete any supplementary schedules that apply to this return.
        </p>
      </div>

      {isCheckingRequirements && (
        <div className='flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4'>
          <div className='h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent' />
          <span className='text-sm text-blue-700'>Checking supplementary requirements...</span>
        </div>
      )}

      <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
        <div className='flex gap-3'>
          <div className='text-sm text-amber-800'>
            <p className='font-medium text-start'>When are supplementary pages needed?</p>
            <ul className='mt-1 list-disc pl-4 space-y-0.5 text-xs text-start'>
              <li><strong>CT600A:</strong> Close company with loans or advances to participators (s.455 CTA 2010)</li>
              <li><strong>CT600C:</strong> Company claiming group relief</li>
              <li><strong>CT600L:</strong> Company claiming R&D tax relief or credit</li>
            </ul>
          </div>
        </div>
      </div>

      <div className='space-y-4'>
        {/* CT600A - Loans to Participators */}
        <SupplementaryCard
          id='ct600a'
          title='CT600A Loans to Participators'
          description='Required if the company has made loans or advances to participators that are outstanding at the end of the accounting period.'
          enabled={supplementary.requirements.ct600a}
          onToggle={(v) => updateRequirement('ct600a', v)}
        >
          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <h4 className='text-sm font-medium text-gray-900'>Outstanding Loans</h4>
              <button
                type='button'
                onClick={addLoan}
                className='flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 transition-colors'
              >
                <Plus className='h-3.5 w-3.5' /> Add Loan
              </button>
            </div>

            {supplementary.ct600a.outstandingLoans.length === 0 && (
              <p className='text-sm text-gray-500 italic'>No loans added yet. Click "Add Loan" to begin.</p>
            )}

            {supplementary.ct600a.outstandingLoans.map((loan, index) => (
              <div key={index} className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
                <div className='mb-3 flex items-center justify-between'>
                  <span className='text-xs font-medium text-gray-500'>Loan {index + 1}</span>
                  <button
                    type='button'
                    onClick={() => removeLoan(index)}
                    className='text-red-500 hover:text-red-700'
                  >
                    <Trash2 className='h-4 w-4' />
                  </button>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
                  <div>
                    <label className='mb-1 block text-xs font-medium text-gray-700'>Participator Name</label>
                    <input
                      type='text'
                      value={loan.participatorName}
                      onChange={(e) => updateLoan(index, 'participatorName', e.target.value)}
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                    />
                  </div>
                  <div>
                    <label className='mb-1 block text-xs font-medium text-gray-700'>Loan Amount</label>
                    <div className='relative'>
                      <span className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400'>£</span>
                      <input
                        type='text'
                        value={loan.loanAmount ? formatAmount(loan.loanAmount) : ''}
                        onChange={(e) => updateLoan(index, 'loanAmount', parseAmount(e.target.value))}
                        placeholder='0.00'
                        className='w-full rounded-lg border border-gray-300 px-3 py-2 pl-7 text-sm text-right text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                      />
                    </div>
                  </div>
                  <div>
                    <label className='mb-1 block text-xs font-medium text-gray-700'>Date of Loan</label>
                    <input
                      type='date'
                      value={loan.dateOfLoan}
                      onChange={(e) => updateLoan(index, 'dateOfLoan', e.target.value)}
                      className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                    />
                  </div>
                </div>
                <div className='mt-3'>
                  {/* HMRC requires the participator's address on CT600A. */}
                  <label className='mb-1 block text-xs font-medium text-gray-700'>Participator Address</label>
                  <input
                    type='text'
                    value={loan.participatorAddress}
                    onChange={(e) => updateLoan(index, 'participatorAddress', e.target.value)}
                    className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                </div>
              </div>
            ))}

            <div className='grid grid-cols-2 gap-4 pt-2'>
              <CurrencyInput
                label='Total Outstanding Loans'
                value={supplementary.ct600a.totalOutstandingLoans}
                onChange={(v) => updateCt600a('totalOutstandingLoans', v)}
              />
              <CurrencyInput
                label='Tax Chargeable (s.455)'
                value={supplementary.ct600a.taxChargeable}
                onChange={(v) => updateCt600a('taxChargeable', v)}
              />
            </div>
          </div>
        </SupplementaryCard>

        {/* CT600C - Group Relief */}
        <SupplementaryCard
          id='ct600c'
          title='CT600C Group Relief'
          description='Required if the company is claiming to surrender or receive group relief.'
          enabled={supplementary.requirements.ct600c}
          onToggle={(v) => updateRequirement('ct600c', v)}
        >
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>Group Company Name</label>
              <input
                type='text'
                value={supplementary.ct600c.groupCompanyName}
                onChange={(e) => updateCt600c('groupCompanyName', e.target.value)}
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            </div>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>Group Company UTR</label>
              <input
                type='text'
                value={supplementary.ct600c.groupCompanyUtr}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
                  updateCt600c('groupCompanyUtr', digits)
                }}
                maxLength={10}
                placeholder='10-digit UTR'
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            </div>
            <CurrencyInput
              label='Relief Amount'
              value={supplementary.ct600c.reliefAmount}
              onChange={(v) => updateCt600c('reliefAmount', v)}
            />
          </div>
        </SupplementaryCard>

        {/* CT600L - R&D */}
        <SupplementaryCard
          id='ct600l'
          title='CT600L Research & Development'
          description='Required if the company is claiming R&D enhanced expenditure or R&D tax credit.'
          enabled={supplementary.requirements.ct600l}
          onToggle={(v) => updateRequirement('ct600l', v)}
        >
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <CurrencyInput
              label='R&D Expenditure'
              value={supplementary.ct600l.rdExpenditure}
              onChange={(v) => updateCt600l('rdExpenditure', v)}
            />
            <CurrencyInput
              label='R&D Enhancement'
              value={supplementary.ct600l.rdEnhancement}
              onChange={(v) => updateCt600l('rdEnhancement', v)}
            />
            <CurrencyInput
              label='R&D Tax Credit'
              value={supplementary.ct600l.rdCredit}
              onChange={(v) => updateCt600l('rdCredit', v)}
            />
          </div>
        </SupplementaryCard>
      </div>
    </div>
  )
}
